/**
 * The Benchmarks tab's "Forecast skill" block (lane P2 SCORE): the live
 * scorecard of the brain's predictive organ — rank / Pearson IC, central-50 /
 * central-90 coverage and pinball skill vs a naive baseline per horizon over
 * rolling 7 and 30 days, plus a per-symbol breakdown — with an explicit
 * "no forecasts" / "insufficient data" state. All derivation lives in
 * `forecastSkillView.ts`; this file only builds DOM.
 */

import type { BenchmarksDocument } from "../types/incremental";
import { attachTooltip } from "../ui/tooltip";
import { toneClass, type ToneText } from "../format/number";
import {
  SKILL_COLUMN_TIPS,
  buildForecastSkillState,
  type CalibrationFlag,
  type ForecastSkillRow,
} from "./forecastSkillView";

const BLOCK_TIP =
  "Live scorecard of the brain's forecaster (GRU-A / GRU-B / linear ensemble): each wake's " +
  "quantile forecast of the standardized forward return at each horizon, scored against the " +
  "realized move between sane quote mids once the horizon has elapsed — never before. Male " +
  "forecasts never realize across a market close.";

function cell(text: string, className = ""): HTMLTableCellElement {
  const td = document.createElement("td");
  td.textContent = text;
  if (className) td.className = className;
  return td;
}

function toneCell(value: ToneText): HTMLTableCellElement {
  return cell(value.text, toneClass(value.tone));
}

function flagCell(text: string, flag: CalibrationFlag): HTMLTableCellElement {
  return cell(text, flag === "ok" ? "obs-value-up" : flag === "off" ? "obs-skill-off" : "");
}

function headerRow(columns: readonly [string, string][]): HTMLTableRowElement {
  const head = document.createElement("tr");
  for (const [label, tip] of columns) {
    const th = document.createElement("th");
    th.textContent = label;
    if (tip) attachTooltip(th, tip);
    head.appendChild(th);
  }
  return head;
}

function skillRow(windowName: string, row: ForecastSkillRow, first: boolean): HTMLTableRowElement {
  const tr = document.createElement("tr");
  if (row.insufficient) tr.className = "obs-skill-row-thin";
  tr.appendChild(cell(first ? windowName : ""));
  tr.appendChild(cell(row.horizonLabel));
  tr.appendChild(cell(row.pairs));
  const rankXs = toneCell(row.rankXs);
  if (row.rankXsPositive !== "–") rankXs.textContent = `${row.rankXs.text} (${row.rankXsPositive})`;
  tr.appendChild(rankXs);
  tr.appendChild(toneCell(row.rankPooled));
  tr.appendChild(toneCell(row.pearsonPooled));
  tr.appendChild(flagCell(row.coverage50, row.coverage50Flag));
  tr.appendChild(flagCell(row.coverage90, row.coverage90Flag));
  tr.appendChild(toneCell(row.pinballSkill));
  if (row.insufficient)
    attachTooltip(tr, "Insufficient data: fewer than 30 realized pairs at this horizon.");
  return tr;
}

/** The block for one benchmarks document, or `null` when it carries no scorecard. */
export function buildForecastSkill(doc: BenchmarksDocument): HTMLElement | null {
  const state = buildForecastSkillState(doc.forecast_skill);
  if (state.kind === "absent") return null;
  const block = document.createElement("section");
  block.className = "obs-skill";
  const head = document.createElement("div");
  head.className = "obs-bench-verdict-head";
  const title = document.createElement("span");
  title.textContent = "Forecast skill";
  attachTooltip(title, BLOCK_TIP);
  const pill = document.createElement("span");
  const ok = state.kind === "ready" && state.status === "ok";
  pill.className = `obs-bench-verdict-pill ${ok ? "obs-bench-verdict-pass" : "obs-bench-verdict-pending"}`;
  pill.textContent = ok ? "Live" : state.kind === "pending" ? state.title : "Insufficient data";
  head.appendChild(title);
  head.appendChild(pill);
  block.appendChild(head);

  if (state.kind === "pending") {
    const message = document.createElement("div");
    message.className = "obs-panel-hint";
    message.textContent = state.message;
    block.appendChild(message);
  } else {
    if (state.message) {
      const message = document.createElement("div");
      message.className = "obs-panel-hint";
      message.textContent = state.message;
      block.appendChild(message);
    }
    const table = document.createElement("table");
    table.className = "obs-bench-table obs-skill-table";
    table.appendChild(
      headerRow([
        ["Window", "Rolling UTC days of forecast time."],
        ["Horizon", ""],
        ["Pairs", SKILL_COLUMN_TIPS["pairs"] ?? ""],
        ["Rank IC (x-sec)", SKILL_COLUMN_TIPS["rankXs"] ?? ""],
        ["Rank IC (pooled)", SKILL_COLUMN_TIPS["rankPooled"] ?? ""],
        ["Pearson IC", SKILL_COLUMN_TIPS["pearsonPooled"] ?? ""],
        ["Cov 50", SKILL_COLUMN_TIPS["coverage50"] ?? ""],
        ["Cov 90", SKILL_COLUMN_TIPS["coverage90"] ?? ""],
        ["Pinball skill", SKILL_COLUMN_TIPS["pinballSkill"] ?? ""],
      ]),
    );
    for (const window of state.windows) {
      window.rows.forEach((row, index) => {
        const tr = skillRow(window.name, row, index === 0);
        if (index === 0) attachTooltip(tr.cells[0] as HTMLElement, window.range);
        table.appendChild(tr);
      });
    }
    block.appendChild(table);

    if (state.symbols.length > 0 && state.symbolWindow) {
      const details = document.createElement("details");
      details.className = "obs-skill-symbols";
      const summary = document.createElement("summary");
      summary.textContent = `Per symbol (${state.symbolWindow}, Pearson IC by horizon)`;
      details.appendChild(summary);
      const symbols = document.createElement("table");
      symbols.className = "obs-bench-table";
      const longest = state.horizonLabels[state.horizonLabels.length - 1] ?? "";
      symbols.appendChild(
        headerRow([
          ["Symbol", ""],
          ["Pairs", SKILL_COLUMN_TIPS["pairs"] ?? ""],
          ...state.horizonLabels.map((label): [string, string] => [
            `IC ${label}`,
            SKILL_COLUMN_TIPS["pearsonPooled"] ?? "",
          ]),
          [`Cov 90 (${longest})`, SKILL_COLUMN_TIPS["coverage90"] ?? ""],
        ]),
      );
      for (const symbol of state.symbols) {
        const tr = document.createElement("tr");
        tr.appendChild(cell(symbol.symbol));
        tr.appendChild(cell(symbol.pairs));
        for (const ic of symbol.ics) tr.appendChild(toneCell(ic));
        tr.appendChild(cell(symbol.coverage90));
        symbols.appendChild(tr);
      }
      details.appendChild(symbols);
      block.appendChild(details);
    }
  }
  const footer = document.createElement("div");
  footer.className = "obs-skill-footer";
  footer.textContent = state.footer;
  block.appendChild(footer);
  return block;
}
